import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { SparklesIcon } from '@heroicons/react/outline'

const StatusBadge = ({
    displayClass,
    statusDescription,
    extraBadgeClass = '',
}) => {
    const getFormattedText = () => {
        switch (displayClass) {
            case 'On-hold':
                return 'On Hold'
            case 'Awaiting-po':
                return 'Awaiting PO'
            case 'Canceled':
                return 'Canceled'
            case 'Repair Completed':
                return 'Repair Completed'
            case 'Repair-back-in-progress':
                return 'Back in Progress'
            case 'Rescheduled':
                return 'Rescheduled'
            case 'Repair-in-progress':
                return 'In Progress'
            case 'Scheduled':
                return 'Scheduled'
            case 'Received':
                return 'Received'
            case 'Pending':
                return 'Pending'
            case 'New Feature':
                return 'NEW FEATURE'
            case 'Needs Review':
                return 'Needs Review'
            default:
                return displayClass
        }
    }
    // Full class strings written out explicitly so Tailwind JIT can detect them
    // at scan time. Dynamic template literals like `text-${var}` are invisible
    // to the JIT scanner and produce unstyled badges.
    const STATUS_CLASSES = {
        'on-hold':                 { text: 'text-colorsStatusBadges-on-hold-text',                 bg: 'bg-colorsStatusBadges-on-hold-bg' },
        'awaiting-po':             { text: 'text-colorsStatusBadges-awaiting-po-text',             bg: 'bg-colorsStatusBadges-awaiting-po-bg' },
        'canceled':                { text: 'text-colorsStatusBadges-canceled-text',                bg: 'bg-colorsStatusBadges-canceled-bg' },
        'completed':               { text: 'text-colorsStatusBadges-completed-text',               bg: 'bg-colorsStatusBadges-completed-bg' },
        'repair-back-in-progress': { text: 'text-colorsStatusBadges-repair-back-in-progress-text', bg: 'bg-colorsStatusBadges-repair-back-in-progress-bg' },
        'PM-back-in-progress':     { text: 'text-colorsStatusBadges-PM-back-in-progress-text',     bg: 'bg-colorsStatusBadges-PM-back-in-progress-bg' },
        'rescheduled':             { text: 'text-colorsStatusBadges-rescheduled-text',             bg: 'bg-colorsStatusBadges-rescheduled-bg' },
        'repair-in-progress':      { text: 'text-colorsStatusBadges-repair-in-progress-text',      bg: 'bg-colorsStatusBadges-repair-in-progress-bg' },
        'PM-in-progress':          { text: 'text-colorsStatusBadges-PM-in-progress-text',          bg: 'bg-colorsStatusBadges-PM-in-progress-bg' },
        'scheduled':               { text: 'text-colorsStatusBadges-scheduled-text',               bg: 'bg-colorsStatusBadges-scheduled-bg' },
        'received':                { text: 'text-colorsStatusBadges-received-text',                bg: 'bg-colorsStatusBadges-received-bg' },
        'processing':              { text: 'text-colorsStatusBadges-processing-text',              bg: 'bg-colorsStatusBadges-processing-bg' },
        'credit-review':           { text: 'text-colorsStatusBadges-credit-review-text',           bg: 'bg-colorsStatusBadges-credit-review-bg' },
        'pending':                 { text: 'text-colorsStatusBadges-pending-text',                 bg: 'bg-colorsStatusBadges-pending-bg' },
        'draft':                   { text: 'text-colorsStatusBadges-draft-text',                   bg: 'bg-colorsStatusBadges-draft-bg' },
        'declined':                { text: 'text-colorsStatusBadges-declined-text',                bg: 'bg-colorsStatusBadges-declined-bg' },
        'expired':                 { text: 'text-colorsStatusBadges-expired-text',                 bg: 'bg-colorsStatusBadges-expired-bg' },
        'awaiting-approval':       { text: 'text-colorsStatusBadges-awaiting-approval-text',       bg: 'bg-colorsStatusBadges-awaiting-approval-bg' },
        'approved':                { text: 'text-colorsStatusBadges-approved-text',                bg: 'bg-colorsStatusBadges-approved-bg' },
    }

    const classMappings = {
        'Repair Completed': 'completed',
        'PM Completed': 'completed',
        'On Hold': 'on-hold',
        'Awaiting PO': 'awaiting-po',
        'Repair Back In Progress': 'repair-back-in-progress',
        'PM Back In Progress': 'PM-back-in-progress',
        'Repair In Progress': 'repair-in-progress',
        'PM In Progress': 'PM-in-progress',
        'Awaiting Approval': 'awaiting-approval',
        'Credit Review': 'credit-review',
        Pending: 'pending',
        'New Feature': 'received',
        'Needs Review': 'on-hold',
    }

    const getActiveClass = () =>
        classMappings[displayClass] || displayClass?.toLowerCase()

    const { text: textColor = '', bg: bgColor = '' } =
        STATUS_CLASSES[getActiveClass()] || {}
    const displayClasses =
        displayClass === 'New Feature'
            ? 'py-3 lg:py-2.5 rounded-md border border-[#5925DC1A]'
            : 'py-1 lg:py-1 rounded-xl'

    return (
        <div className="flex lg:items-start items-end justify-center flex-col lg:h-auto h-auto min-h-10">
            <p
                className={clsx(
                    `w-max text-xs font-medium text-center px-3 ${displayClasses} lg:mb-0 flex items-center justify-center`,
                    textColor,
                    bgColor,
                    extraBadgeClass,
                )}
            >
                {displayClass === 'New Feature' && (
                    <SparklesIcon className="w-4 h-4 mr-1" />
                )}
                {getFormattedText()}
            </p>

            {statusDescription && (
                <span className="text-sm font-normal text-textDarkGray truncate max-w-full mt-1">
                    {statusDescription}
                </span>
            )}
        </div>
    )
}

StatusBadge.propTypes = {
    displayClass: PropTypes.string,
    statusDescription: PropTypes.oneOfType([PropTypes.string, PropTypes.any]),
    extraBadgeClass: PropTypes.string,
}

export default StatusBadge
